import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import client, { makeGet } from "@culturehq/client";
import { Loader, Pagination, Table, Warning, Icon } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination } from "app/hooks";
import { isMultiImageStory, isTarget, t, Trans } from "app/utils";
import { isAdmin } from "app/permissions";

import StoryShareFacebookInstagram from "components/common/StoryShareFacebookInstagram";
import { Time } from "components/common/Time";
import LightboxStories from "components/common/gallery/LightboxStories";
import PageHeader from "components/display/PageHeader";
import getUserName from "app/utils/getUserName";
import LightboxStoryQuickOptions from "components/common/gallery/LightboxStoryQuickOptions";
import useLatestMedia from "app/hooks/useLatestMedia";
import StoryPromptTitle from "./StoryPromptTitle";
import ShareStoryOptions from "../ShareStoryOptions";
import { deleteEntity, updateEntity } from "../mutations";
import ApproveForJobMappingButton from "./ApproveForJobMappingButton";
import { filtersToQuery, queryToOptions } from "../EmbedStoriesSlider";
import StoryFilterBar from "../StoryFilterBar";
import ApproveLandingPageButton from "./ApproveLandingPageButton";

const updateApprovedContent = updateEntity("approvedForSocial");
const deleteApprovedContent = deleteEntity("approvedForSocial");

const ApproveContentRow = ({
  approvedContent,
  history,
  landingPages,
  onReviewClicked,
  onUpdateApprovedForSocial,
  onDelete,
  onStoryUpdate
}) => {
  const [qrToshare, setQrToshare] = useState([]);

  const { profile } = useProfile();
  const { entity: story, creator, createdAt } = approvedContent;

  const {
    isFetching: mediaIsFetching,
    lastMediaVersion,
    thirdPartyMedias,
    onThirdPartyMediasReload,
    onUpdateDraftVersion,
    onDiscardDraft
  } = useLatestMedia(story, profile, { landingPage: false });
  const isLastMediaVersionProcessing = lastMediaVersion?.status === "processing";

  const goToPublishedContent = () => {
    history.push(`/admin/story-reviews/published?id=${approvedContent.id}`);
  };

  const onShareToQRLink = values => {
    if (values.length > 0) {
      setQrToshare(values);
    } else {
      setQrToshare([]);
    }
  };

  const handleQRClose = () => {
    setQrToshare([]);
  };

  return (
    <tr>
      {qrToshare.length > 0 && (
        <StoryShareFacebookInstagram
          story={story}
          initialOpen
          onClose={handleQRClose}
          qrToshare={qrToshare}
        />
      )}
      <td className="StoryTable-clickable" onClick={() => onReviewClicked(approvedContent)}>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={story.media.thumbnail || story.media.url} alt={story.media.alt || story.question.question} />
          {story.media.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
          {isMultiImageStory(story) && <Icon className="StoryTable-Details__ImagesIcon" icon="images" />}
        </div>
      </td>
      <StoryPromptTitle
        approvedContent={approvedContent}
        onReviewClicked={onReviewClicked}
        story={story}
        tab="approved"
      />
      <td className="StoryTable-clickable" onClick={() => onReviewClicked(approvedContent)}>
        <h4 className="User">{getUserName(story.creator)}</h4>
        {!isTarget(profile.organization.name) && story.creator.companies?.map(company => (
          <div className="icon-building">
            <Icon icon="building" />
            {company.name}
          </div>
        ))}
        <p><Time time={story.createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      <td className="StoryTable-clickable" onClick={() => onReviewClicked(approvedContent)}>
        <h4 className="User">{getUserName(creator)}</h4>
        <p><Time time={createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      {approvedContent.approvedForSocialMessages.length > 0 ? (
        <td className="StoryTable-clickable Shares-Number" onClick={() => goToPublishedContent()}>
          {approvedContent.approvedForSocialMessages.length}
        </td>
      ) : (
        <td className="StoryTable-clickable">
          {approvedContent.approvedForSocialMessages.length}
        </td>
      )}
      <td className="Actions-column">
        <LightboxStoryQuickOptions
          className="Admin-QuickOptions"
          isLastMediaVersionProcessing={isLastMediaVersionProcessing}
          story={story}
          thirdPartyMediaIsFetching={mediaIsFetching}
          thirdPartyMedias={thirdPartyMedias}
          onDelete={onDelete}
          onUpdate={onStoryUpdate}
          onAnotherModalOpen={() => {}}
          onPauseVideo={() => {}}
          onThirdPartyMediasReload={onThirdPartyMediasReload}
          onUpdateDraftVersion={onUpdateDraftVersion}
          onDiscardDraft={onDiscardDraft}
        />
        <div className="admin-story-action-buttons">
          <ShareStoryOptions
            approvedContent={approvedContent}
            className="Approved-actions__Share"
            story={story}
            onShareToQRLink={onShareToQRLink}
          />
          {profile.activeLandingPage && (
            <ApproveLandingPageButton
              approvedContent={approvedContent}
              externalState={story.externalState}
              landingPages={landingPages}
              onUpdateApprovedForSocial={onUpdateApprovedForSocial}
            />
          )}
          <ApproveForJobMappingButton
            approvedForSocial={approvedContent}
            onUpdate={onUpdateApprovedForSocial}
          />
        </div>
      </td>
    </tr>
  );
};

const StoriesApproved = ({ match }) => {
  const { profile } = useProfile();
  const [page, onPageClick] = usePagination();
  const history = useHistory();
  const [activeApprovedContent, setActiveApprovedContent] = useState();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const {
    fetching,
    json: { approvedForSocial = [], pagination = {} },
    onUpdate: onApprovedContentUpdate
  } = useFetch(
    () => {
      const query = filtersToQuery(selectedFilters);
      const params = { ...queryToOptions(query), page: page || 1 };
      const [bodySearch] = selectedFilters;
      if (typeof bodySearch === "string") params.bodySearch = bodySearch;
      return makeGet("/stories/approved", params);
    },
    [page, selectedFilters]
  );

  const { fetching: fetchingLandingPage, json: { landingPages = [] } } = useFetch(
    () => client.listLandingPages(),
    []
  );

  const onUpdate = approved => onApprovedContentUpdate(updateApprovedContent(approved));
  const onDelete = approved => onApprovedContentUpdate(deleteApprovedContent(approved));

  useEffect(
    () => {
      const getStory = async id => {
        try {
          const { story } = await client.getStory({ storyId: id });
          if (story.externalState === "approved"
            || story.externalState === "landing_page"
            || story.externalState === "linkedin"
            || story.externalState === "approved_for_everything") {
            setActiveApprovedContent({ entity: story });
            setOpenModal(true);
          } else {
            history.push("/admin/story-reviews/approved");
          }
        } catch (error) {
          history.push("/admin/story-reviews/approved");
        }
      };

      if (match.params.storyId) {
        getStory(match.params.storyId);
      }
    },
    [match.params.storyId, history]
  );

  const handleReviewClicked = approvedContent => {
    setActiveApprovedContent(approvedContent);
    setOpenModal(true);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    const entityToUpdate = approvedForSocial
      .find(approvedContent => approvedContent.entity.id === story.id);
    onUpdate({
      ...entityToUpdate,
      entity: { ...entityToUpdate.entity, commentCount: numberOfComments }
    });
  };

  const handleStoryRemove = storyId => {
    const entityToUpdate = approvedForSocial
      .find(approvedContent => approvedContent.entity.id === storyId);
    if (entityToUpdate) {
      onDelete(entityToUpdate);
    }
    setOpenModal(false);
    setActiveApprovedContent(undefined);
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    const entityToUpdate = approvedForSocial
      .find(approvedContent => approvedContent.entity.id === storyId);
    onUpdate({
      ...entityToUpdate,
      entity: { ...entityToUpdate.entity, cheerForUser, cheersCount }
    });
  };

  const onStoryUpdate = updatedStory => {
    const entityToUpdate = approvedForSocial
      .find(approvedContent => approvedContent.entity.id === updatedStory.id);
    onUpdate({ ...entityToUpdate, entity: updatedStory });
    setActiveApprovedContent({ ...entityToUpdate, entity: updatedStory });
  };

  const handleClose = () => {
    if (match.params.storyId) history.push("/admin/story-reviews/approved");
    setOpenModal(false);
  };

  const handleStoryChange = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = approvedForSocial.length - 1;
    }

    if (goToIndex > approvedForSocial.length - 1) {
      target = 0;
    }

    setActiveApprovedContent(approvedForSocial[target]);
    setOpenModal(true);
  };

  const handleUpdateApprovedForSocial = updatedApprovedContent => {
    onUpdate(updatedApprovedContent);
    setActiveApprovedContent(updatedApprovedContent);
  };

  const handleSelectedTags = (updatedOptions, _) => {
    setSelectedFilters(updatedOptions);
  };

  return (
    <div className="padding-left-15px padding-right-15px StoryApprovals">
      <LightboxStories
        className="gallery-lightbox--Story-Review"
        landingPages={landingPages}
        modalIsOpen={openModal}
        modalStyle={{ overlay: { overflow: "hidden" } }}
        activeStory={activeApprovedContent?.entity}
        currentUserAnswered
        stories={approvedForSocial.map(content => content.entity)}
        onClose={handleClose}
        onCommentsChange={handleCommentsChange}
        onDelete={handleStoryRemove}
        onStoryCheerToggle={handleStoryCheerToggle}
        onStoryUpdate={onStoryUpdate}
        onStoryChange={handleStoryChange}
        approvedContent={activeApprovedContent}
        onUpdateApprovedForSocial={handleUpdateApprovedForSocial}
        approvedVersion
      />
      <PageHeader title={t("Stories Approved for External")}>
        <Trans>
          Below is the list of stories that have been approved for use externally.
          You can use the button below to publish the stories to be shared on social
          and/or displayed on your landing page.
        </Trans>
        <div style={{ marginTop: "15px", width: "100%" }}>
          {isAdmin(profile) && (
            <Link className="Link-Social__Media" to="/profile/settings/social">
              <Icon icon="plus" />
              <Trans>Link social accounts</Trans>
            </Link>
          )}
          <StoryFilterBar
            creatable
            selectedOptions={selectedFilters}
            handleSelectedTags={handleSelectedTags}
            icon="ios-search-strong"
          />
        </div>
      </PageHeader>
      <Loader loading={fetching || fetchingLandingPage}>
        <Table className="StoryTable StoryTable__Approved">
          <thead>
            <tr>
              <th className="thumb">
                <Trans>Story</Trans>
              </th>
              <th />
              <th className="users">
                <Trans>User</Trans>
              </th>
              <th className="users">
                <Trans>Approved</Trans>
              </th>
              <th className="shares-number">
                <Trans># Shares</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {approvedForSocial.map(content => (
              <ApproveContentRow
                key={content.id}
                approvedContent={content}
                history={history}
                landingPages={landingPages}
                onReviewClicked={handleReviewClicked}
                onUpdateApprovedForSocial={handleUpdateApprovedForSocial}
                onDelete={handleStoryRemove}
                onStoryUpdate={onStoryUpdate}
              />
            ))}
          </tbody>
        </Table>
        {approvedForSocial.length < 1 && (
          <Warning>
            <Trans>
              No stories have been approved yet.
            </Trans>
          </Warning>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

export default StoriesApproved;
